import { create } from 'zustand';
import {
  archiveFacility,
  createFacility,
  getFacility,
  getFacilityIncomingOrders,
  listFacilities,
  updateFacility,
} from '../services/facility.service';
import type {
  Facility,
  FacilityIncomingOrder,
  FacilityInput,
  FacilityPagination,
  FacilityQuery,
  FacilityUpdateInput,
} from '../types/facility.type';

interface FacilityState {
  facilities: Facility[];
  selectedFacility: Facility | null;
  total: number;
  page: number;
  limit: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  selectedFacilityNotFound: boolean;
  incomingOrders: FacilityIncomingOrder[];
  incomingOrdersTotal: number;
  incomingOrdersPage: number;
  incomingOrdersLimit: number;
  incomingOrdersLoading: boolean;
  clearError: () => void;
  fetchFacilities: (query: FacilityQuery) => Promise<FacilityPagination | null>;
  fetchFacility: (organizationId: string, facilityId: string) => Promise<Facility | null>;
  createFacility: (organizationId: string, input: FacilityInput) => Promise<Facility | null>;
  updateFacility: (
    organizationId: string,
    facilityId: string,
    input: FacilityUpdateInput,
  ) => Promise<Facility | null>;
  archiveFacility: (organizationId: string, facilityId: string) => Promise<boolean>;
  fetchIncomingOrders: (
    organizationId: string,
    facilityId: string,
    query?: { page?: number; limit?: number },
  ) => Promise<void>;
}

function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } }).response?.status === 404;
}

export const useFacilityStore = create<FacilityState>((set) => ({
  facilities: [],
  selectedFacility: null,
  total: 0,
  page: 1,
  limit: 20,
  isLoading: false,
  isSaving: false,
  error: null,
  selectedFacilityNotFound: false,
  incomingOrders: [],
  incomingOrdersTotal: 0,
  incomingOrdersPage: 1,
  incomingOrdersLimit: 20,
  incomingOrdersLoading: false,
  clearError: () => set({ error: null }),

  fetchFacilities: async (query) => {
    set({ isLoading: true, error: null });
    try {
      const result = await listFacilities(query);
      set({ ...result, isLoading: false });
      return result;
    } catch (error) {
      if (isNotFound(error)) {
        const emptyResult: FacilityPagination = {
          facilities: [],
          total: 0,
          page: query.page ?? 1,
          limit: query.limit ?? 20,
        };
        set({ ...emptyResult, isLoading: false, error: null });
        return emptyResult;
      }
      set({ isLoading: false, error: errorMessage(error, 'Failed to load facilities') });
      return null;
    }
  },

  fetchFacility: async (organizationId, facilityId) => {
    set({ isLoading: true, error: null, selectedFacilityNotFound: false });
    try {
      const facility = await getFacility(organizationId, facilityId);
      set({ selectedFacility: facility, isLoading: false, selectedFacilityNotFound: false });
      return facility;
    } catch (error) {
      if (isNotFound(error)) {
        set({ selectedFacility: null, isLoading: false, error: null, selectedFacilityNotFound: true });
        return null;
      }
      set({ isLoading: false, error: errorMessage(error, 'Failed to load facility') });
      return null;
    }
  },

  createFacility: async (organizationId, input) => {
    set({ isSaving: true, error: null });
    try {
      const facility = await createFacility(organizationId, input);
      set((state) => ({ facilities: [facility, ...state.facilities], total: state.total + 1, isSaving: false }));
      return facility;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to create facility') });
      return null;
    }
  },

  updateFacility: async (organizationId, facilityId, input) => {
    set({ isSaving: true, error: null });
    try {
      const facility = await updateFacility(organizationId, facilityId, input);
      set((state) => ({
        facilities: state.facilities.map((row) => (row.id === facilityId ? facility : row)),
        selectedFacility: state.selectedFacility?.id === facilityId ? facility : state.selectedFacility,
        isSaving: false,
      }));
      return facility;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to update facility') });
      return null;
    }
  },

  archiveFacility: async (organizationId, facilityId) => {
    set({ isSaving: true, error: null });
    try {
      await archiveFacility(organizationId, facilityId);
      set((state) => ({
        facilities: state.facilities.filter((row) => row.id !== facilityId),
        total: Math.max(0, state.total - 1),
        selectedFacility: state.selectedFacility?.id === facilityId ? null : state.selectedFacility,
        isSaving: false,
      }));
      return true;
    } catch (error) {
      set({ isSaving: false, error: errorMessage(error, 'Failed to archive facility') });
      return false;
    }
  },

  fetchIncomingOrders: async (organizationId, facilityId, query = {}) => {
    set({ incomingOrdersLoading: true, error: null });
    try {
      const result = await getFacilityIncomingOrders(organizationId, facilityId, query);
      set({
        incomingOrders: result.orders,
        incomingOrdersTotal: result.total,
        incomingOrdersPage: result.page,
        incomingOrdersLimit: result.limit,
        incomingOrdersLoading: false,
      });
    } catch (error) {
      if (isNotFound(error)) {
        set({
          incomingOrders: [],
          incomingOrdersTotal: 0,
          incomingOrdersPage: query.page ?? 1,
          incomingOrdersLimit: query.limit ?? 20,
          incomingOrdersLoading: false,
        });
        return;
      }
      set({ incomingOrdersLoading: false, error: errorMessage(error, 'Failed to load incoming orders') });
    }
  },
}));
